// @flow

import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { translate } from 'react-i18next';
import { Theme } from 'statsradio/themes/v2';
import TitledCard from 'common/components/cards/TitledCard';
import { displayPercent, displayCurrency } from 'common/components/Number';
import { roundNumber } from 'common/helpers/math';
import SwitchToggle from 'common/components/ui/SwitchToggle';
import RadioCampaignInvoice from '../domain/RadioCampaignInvoice';

const Box = styled.div`
  margin: auto;
  font-size: 16px;
  line-height: 24px;

  & > table {
    width: 100%;
    line-height: inherit;
    text-align: left;
    border-collapse: collapse;
  }

  & > table tbody tr td:last-child, 
  & > table tfoot tr td:last-child {
    text-align: right;
  }
`;

const STable = styled.table`
  table-layout: fixed;
`;

const Row = styled.tr`
  ${props => props.dark && `
    background-color: ${props.theme.V2.colors.table.separator};
  `}

  &:hover {
    ${props => props.dark && `
      background-color: ${props.theme.V2.colors.table.hoverOnDark} !important;
    `}
  }
`;

const SName = styled.td`
  width: 50%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const SSpots = styled.td`
  width: 10%
`;

const SCostPerSpots = styled.td`
  width: 10%
`;

const SCost = styled.td`
  width: 10%
`;

const STotal = styled.td`
  white-space: nowrap;
`;

const CustomerRow = styled(Row)`
  border-bottom: 1px solid black;
  padding-top: 8px;
`;


const PaddedRow = styled(Row)`
  & > td:first-child {
    padding-left: 16px;
  }
`;

const Total = styled.span`
  border-top: 3px solid black;
`;

interface Props {
  invoice: RadioCampaignInvoice;
  onBilledChange: (billed: boolean) => void;
  t: any;
  theme: typeof Theme;
}

function RadioInvoice({ invoice, theme, onBilledChange, t }: Props) {
  const percent = displayPercent(invoice.salesCommission);

  return (
    <TitledCard
      title={invoice.radio}
      collapsable
      defaultCollapsed
      TitleExtraRightItem={
        <SwitchToggle
          label={t('billed')}
          color="secondary"
          onChange={onBilledChange}
          defaultChecked={invoice.isBilled()}
        />
      }
    >
      <Box>
        <STable>
          <thead>
            <tr>
              <th style={{ width: '30%' }} />
              <th style={{ width: '10%' }} />
              <th style={{ width: '10%' }} />
              <th style={{ width: '10%' }} />
              <th style={{ width: '20%' }} />
            </tr>
          </thead>
          <tbody>
            { invoice.customers.map((customer, index) => {
              const isEven = (index + 1) % 2 === 0;

              return (
                <React.Fragment key={customer.name}>
                  <CustomerRow dark={isEven}>
                    <td colSpan="4">{customer.name}</td>
                    <td />
                  </CustomerRow>
                  { customer.campaigns.map(({ name, numberOfSpots, numberOfPlayedSpots, cost }) => {
                    const costPerSpot = numberOfSpots === 0 ? cost.amount : roundNumber(cost.amount / numberOfSpots, 2);
                    return (
                      <PaddedRow key={name} dark={isEven}>
                        <SName>{name}</SName>
                        <SSpots>{t('spots', { count: numberOfSpots })}</SSpots>
                        <SSpots>{t('playedSpots', { count: numberOfPlayedSpots })}</SSpots>
                        <SCostPerSpots>{t('costPerSpot', { value: displayCurrency(costPerSpot) })}</SCostPerSpots>
                        <SCost>{displayCurrency(cost.amount)}</SCost>
                      </PaddedRow>
                    );
                  })}
                  <PaddedRow dark={isEven}>
                    <td colSpan="5">{`${displayCurrency(customer.total.amount)} x ${percent} = ${displayCurrency(customer.salesCommissionTotal.amount)}`}</td>
                  </PaddedRow>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="4" />
              <STotal>
                <Total>{`${t('campaignsTotal')}: ${displayCurrency(invoice.total.amount)}`}</Total>
              </STotal>
            </tr>
            <tr>
              <td colSpan="4" />
              <STotal>{`${t('salesCommissionsTotal')}: ${displayCurrency(invoice.salesCommissionTotal.amount)}`}</STotal>
            </tr>
          </tfoot>
        </STable>
      </Box>
    </TitledCard>
  );
}

export default withTheme(translate('invoicing')(RadioInvoice));



// WEBPACK FOOTER //
// ./src/statsradio/modules/billing/components/RadioInvoice.js